import { Condition, Question, FlowBlockQuestion } from '@staffino/model';
import { ConditionEvaluatorContext } from '../condition-evaluator-context';
import FlowDefinitionTraverser from '../../flow-definition-traverser';
import { QuestionObject } from '../../flow-bridge';

const range = (start, stop, step = 1) =>
  Array(Math.ceil((stop - start) / step))
    .fill(start)
    .map((x, y) => x + y * step);

function defaultNeutralSentimentBound(metric: Question['metric']) {
  switch (metric) {
    case 'NPS':
      return 7;
    default:
      return 3;
  }
}

function defaultPositiveSentimentBound(metric: Question['metric']) {
  switch (metric) {
    case 'NPS':
      return 9;
    default:
      return 5; // TODO: this depends on the question too
  }
}

function calculateScoreList(question: Question, value) {
  switch (value) {
    case 'negative':
      return range(
        0,
        question.neutralSentimentBound ??
          defaultNeutralSentimentBound(question.metric),
      );

    case 'neutral':
      return range(
        question.neutralSentimentBound ??
          defaultNeutralSentimentBound(question.metric),
        question.positiveSentimentBound ??
          defaultPositiveSentimentBound(question.metric),
      );

    case 'positive':
      return range(
        question.positiveSentimentBound ??
          defaultPositiveSentimentBound(question.metric),
        11,
      );

    default:
      return [];
  }
}

export default async function answer(
  condition: Condition,
  context: ConditionEvaluatorContext,
): Promise<boolean> {
  const {
    blockId,
    questionType,
    choiceIds,
    choiceIdsOperator,
    operator,
    value,
    commentOperator,
    commentLength,
    comment,
  } = condition[1] ?? {};
  const {
    score,
    choiceIds: selectedChoiceIds,
    comment: selectedComment,
  } = context.stateManager.getAggregatedFormData()[blockId] ?? {};
  const selectedScore = Number.parseInt(score, 10);

  // check choices
  if (choiceIdsOperator === 'empty') {
    return !selectedChoiceIds?.length;
  }
  if (choiceIdsOperator === '!empty') {
    return !!selectedChoiceIds?.length;
  }
  if (choiceIdsOperator === 'contains') {
    const compareSet = new Set(choiceIds);
    return selectedChoiceIds?.some((id) => compareSet.has(id));
  }
  if (choiceIdsOperator === '!contains') {
    const compareSet = new Set(choiceIds);
    return selectedChoiceIds?.every((id) => !compareSet.has(id));
  }
  if (['equals', '=', '=='].includes(choiceIdsOperator)) {
    const a = new Set(choiceIds);
    const b = new Set(selectedChoiceIds);
    // compare sets
    return a.size === b.size && [...a].every((id) => b.has(id));
  }
  if (operator === 'contains') {
    return value?.some((val) => selectedScore === val);
  }
  if (operator === 'empty') {
    return Number.isNaN(selectedScore) && selectedChoiceIds === undefined;
  }
  if (operator === '!empty') {
    return !Number.isNaN(selectedScore) || selectedChoiceIds !== undefined;
  }

  if (commentOperator === '>') {
    return selectedComment?.length > commentLength;
  }
  if (commentOperator === '<') {
    return selectedComment?.length < commentLength;
  }
  if (commentOperator === 'contains') {
    return selectedComment?.includes(comment);
  }
  if (commentOperator === '!contains') {
    return !selectedComment?.includes(comment);
  }
  if (commentOperator === 'empty') {
    return !selectedComment;
  }
  if (commentOperator === '!empty') {
    return !!selectedComment;
  }

  if (['Sentiment', 'FCR'].includes(questionType) && operator === 'equals') {
    return selectedScore === value;
  }
  if (['NPS', 'CSAT'].includes(questionType) && operator === 'equals') {
    // for these operators we need to read neutralSentimentBound and positiveSentimentBound
    const traverser = new FlowDefinitionTraverser(context.definition);
    const block = traverser.getBlockById(blockId);
    if (block?.type === 'question') {
      const { questionId } = block as FlowBlockQuestion;
      const objects =
        questionId &&
        (await context.bridge.load({ objectType: 'question', id: questionId }));
      const question = objects?.[0] as QuestionObject;
      if (question) {
        const scoreList = new Set(calculateScoreList(question, value));
        return scoreList.has(selectedScore);
      }
    }
  }
  return false;
}
